/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import i18next from 'i18next';
import utils from '../services/Utils';

const initialState = {
  loading: false,
  historicalPerformanceData: {
    gro10k: [],
    tabular: [],
  },
  historicalPerformanceError: '',
  calendarYearPerformance: {},
  calendarYearPerformanceError: '',
  timePeriods: [],
  selectedPeriod: 'YTD',
  selectedPeriodFilteredData: [],
  fundName: '',
  indexName: '',
  firstStartDate: '',
  lastEndDate: '',
  fundCurrency: '',
  indexCurrency: '',
  entityInceptionDt: '',
  ucitsFootenote: '',
};
const basePeriods = ['1M', '3M', '6M', '1Y', '3Y', '5Y', '10Y', 'YTD'];

const UCITS_FOOTNOTE_KEYS = {
  BENCHMARK_ONLY: 'UCITS_FOOTNOTE_BENCHMARK_ONLY',
  SHARECLASS_AND_BENCHMARK: 'UCITS_FOOTNOTE_SHARECLASS_AND_BENCHMARK',
  HEDGED_SHARECLASS: 'UCITS_FOOTNOTE_HEDGED_SHARECLASS',
};

function parseUcitsFootnote(ucitsFootenote) {
  if (!ucitsFootenote || typeof ucitsFootenote !== 'string') return null;

  const note = ucitsFootenote.trim();

  // Match the *entire* string so partial matches don't pick the wrong template.

  // 3) "This is a hedged share class, performance displayed in EUR and Benchmark performance displayed in EUR."
  const hedgedShareClass =
    /^This is a hedged share class,\s*performance displayed in\s+([A-Z]{3})\s+and\s+Benchmark performance displayed in\s+([A-Z]{3})\.?$/i.exec(
      note,
    );
  if (hedgedShareClass) {
    return {
      key: UCITS_FOOTNOTE_KEYS.HEDGED_SHARECLASS,
      params: {
        fundCurrency: hedgedShareClass[1].toUpperCase(),
        idxCurrency: hedgedShareClass[2].toUpperCase(),
      },
    };
  }

  // 2) "Share Class performance displayed in USD and Benchmark performance displayed in USD."
  const shareClassAndBenchmark =
    /^Share Class performance displayed in\s+([A-Z]{3})\s+and\s+Benchmark performance displayed in\s+([A-Z]{3})\.?$/i.exec(
      note,
    );
  if (shareClassAndBenchmark) {
    return {
      key: UCITS_FOOTNOTE_KEYS.SHARECLASS_AND_BENCHMARK,
      params: {
        fundCurrency: shareClassAndBenchmark[1].toUpperCase(),
        idxCurrency: shareClassAndBenchmark[2].toUpperCase(),
      },
    };
  }

  // 1) "Benchmark performance displayed in USD."
  const benchmarkOnly = /^Benchmark performance displayed in\s+([A-Z]{3})\.?$/i.exec(note);
  if (benchmarkOnly) {
    return {
      key: UCITS_FOOTNOTE_KEYS.BENCHMARK_ONLY,
      params: { idxCurrency: benchmarkOnly[1].toUpperCase() },
    };
  }

  return null;
}

function getPeriodStart(period, latestDate) {
  const today = new Date(latestDate);

  const getDaysBetween = (from, to) => {
    const diffMs = from.getTime() - to.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const subtractByMonthGapAsDays = monthsBack => {
    const calendarDate = new Date(today);
    calendarDate.setMonth(calendarDate.getMonth() - monthsBack);

    if (calendarDate.getDate() !== today.getDate()) {
      calendarDate.setDate(0); // set to end of previous month
    }

    const dayCount = getDaysBetween(today, calendarDate);

    const result = new Date(today);
    result.setDate(today.getDate() - dayCount);

    return result;
  };

  switch (period) {
    case '1M':
      return subtractByMonthGapAsDays(1);
    case '3M':
      return subtractByMonthGapAsDays(3);
    case '6M':
      return subtractByMonthGapAsDays(6);
    case '1Y': {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    }
    case '3Y': {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - 3);
      return d;
    }
    case '5Y': {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - 5);
      return d;
    }
    case '10Y': {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - 10);
      return d;
    }
    case 'YTD':
      return new Date(today.getFullYear(), 0, 1);
    default:
      return null;
  }
}

export function getAvailableTimePeriods(firstDate, latestDate) {
  const validPeriods = basePeriods.filter(period => {
    const start = getPeriodStart(period, latestDate);
    return start && start.getTime() >= firstDate.getTime();
  });

  validPeriods.push('INC', 'CUSTOM');

  return validPeriods;
}

export const getCalendarPerformanceChartData = createAsyncThunk(
  'historicalPerformance/getCalendarPerformancePerformanceData',
  async ({ isin }, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_DATASPAN_API_URL}/funddetails/calendar_year_performance?isin=${isin}`,
      );

      return response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getPerformanceData = createAsyncThunk(
  'historicalPerformance/getPerformanceData',
  async ({ entity, mode }, thunkAPI) => {
    const { getState, rejectWithValue } = thunkAPI;

    const existingData = getState().app.historicalPerformanceData[mode];
    // Return early if we already have data
    if (Array.isArray(existingData) && existingData.length > 0) {
      return existingData; // short-circuit the API call
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_DATASPAN_API_URL}/funddetails/historical_performance?entity=${entity}&mode=${mode}`,
      );

      const data = await response.json();
      if (data && typeof data === 'object' && data.error) {
        return rejectWithValue(i18next.t('DATA_UNAVAILABLE'));
      }
      return data;
    } catch (error) {
      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.error('API error:', error.message);
      } else {
        // eslint-disable-next-line no-console
        console.error('Unknown error during fetch');
      }
      return rejectWithValue(i18next.t('DATA_UNAVAILABLE'));
    }
  },
);

const appSlice = createSlice({
  initialState,
  name: 'performanceGrowthChart',
  reducers: {
    setLoader: (state, action) => {
      state.loading = action.payload;
    },
    setCalendarPerformance: (state, action) => {
      state.calendarYearPerformance = action.payload;
    },
    setSelectedCustomPeriod: (state, action) => {
      const { period, range } = action.payload;
      const [startStr, endStr] = range;
      const startDate = startStr ? new Date(startStr) : null;
      const endDate = endStr ? new Date(endStr) : null;

      state.selectedPeriod = period;
      state.selectedPeriodFilteredData = state.historicalPerformanceData.gro10k.filter(d => {
        const cob = new Date(d.cobDate);
        return (!startDate || cob >= startDate) && (!endDate || cob <= endDate);
      });
    },
    setSelectedPeriod: (state, action) => {
      const period = action.payload;
      state.selectedPeriod = period;
      const dataArray = state.historicalPerformanceData.gro10k;
      const [, latestDate] = utils.getFirstAndLastDates(dataArray);

      let periodStart;
      if (period === 'YTD') {
        const previousYear = latestDate.getFullYear() - 1;
        const prevYearEntries = dataArray.filter(
          d => new Date(d.cobDate).getFullYear() === previousYear,
        );
        if (prevYearEntries.length > 0) {
          const sortedPrev = prevYearEntries.sort(
            (a, b) => new Date(a.cobDate) - new Date(b.cobDate),
          );
          periodStart = new Date(sortedPrev[sortedPrev.length - 1].cobDate);
        } else {
          periodStart = getPeriodStart(period, latestDate);
        }
      } else {
        periodStart = getPeriodStart(period, latestDate);
      }
      state.selectedPeriodFilteredData = dataArray.filter(d => {
        const cob = new Date(d.cobDate);
        return cob >= periodStart && cob <= latestDate;
      });
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getCalendarPerformanceChartData.pending, state => {
        state.loading = true;
        state.calendarYearPerformance = null;
      })
      .addCase(getCalendarPerformanceChartData.fulfilled, (state, action) => {
        state.calendarYearPerformance = action.payload;
        state.loading = false;
      })
      .addCase(getCalendarPerformanceChartData.rejected, (state, action) => {
        state.calendarYearPerformanceError = action.error.message;
        state.loading = false;
      });

    builder
      .addCase(getPerformanceData.pending, state => {
        state.loading = true;
        state.historicalPerformanceError = null;
      })
      .addCase(getPerformanceData.fulfilled, (state, action) => {
        const { mode } = action.meta.arg;

        if (action.payload.error) {
          return {
            ...state,
            loading: false,
          };
        }

        if (mode === 'gro10k') {
          if (!Array.isArray(action.payload) || action.payload.length === 0) {
            return {
              ...state,
              loading: false,
              historicalPerformanceError: i18next.t('DATA_UNAVAILABLE'),
            };
          }
          const [firstDate, latestDate] = utils.getFirstAndLastDates(action.payload);
          const timePeriods = getAvailableTimePeriods(firstDate, latestDate);
          const availablePeriods = basePeriods.filter(p => timePeriods.includes(p));

          const selectedPeriod =
            availablePeriods.length > 0 ? availablePeriods[availablePeriods.length - 1] : 'INC';

          let periodStart;
          if (selectedPeriod === 'YTD') {
            const previousYear = latestDate.getFullYear() - 1;
            const prevYearEntries = action.payload.filter(
              d => new Date(d.cobDate).getFullYear() === previousYear,
            );
            if (prevYearEntries.length > 0) {
              const sortedPrev = prevYearEntries.sort(
                (a, b) => new Date(a.cobDate) - new Date(b.cobDate),
              );
              periodStart = new Date(sortedPrev[sortedPrev.length - 1].cobDate);
            } else {
              periodStart = getPeriodStart(selectedPeriod, latestDate);
            }
          } else {
            periodStart = getPeriodStart(selectedPeriod, latestDate);
          }

          const selectedPeriodFilteredData = action.payload.filter(d => {
            const cob = new Date(d.cobDate);
            return cob >= periodStart && cob <= latestDate;
          });

          const fundName = selectedPeriodFilteredData.find(d => d.wtTypeID === 1)?.name || '';
          const indexName = selectedPeriodFilteredData.find(d => d.wtTypeID !== 1)?.name || '';
          const fundCurrency =
            selectedPeriodFilteredData.find(d => d.wtTypeID === 1)?.currency || '';
          const indexCurrency =
            selectedPeriodFilteredData.find(d => d.wtTypeID !== 1)?.currency || '';
          const { entityInceptionDt, ucitsFootenote } = selectedPeriodFilteredData[0];
          // TODO - Remove this logic and get the note from Proc response based on iso-Code directly
          const parsedFootnote = parseUcitsFootnote(ucitsFootenote);
          const translatedFootnote = parsedFootnote
            ? i18next.t(parsedFootnote.key, parsedFootnote.params)
            : ucitsFootenote;
          return {
            ...state,
            selectedPeriod,
            selectedPeriodFilteredData,
            timePeriods,
            firstStartDate: firstDate,
            lastEndDate: latestDate,
            fundName,
            indexName,
            ucitsFootenote: translatedFootnote,
            fundCurrency,
            indexCurrency,
            historicalPerformanceData: {
              ...state.historicalPerformanceData,
              [mode]: action.payload,
            },
            loading: false,
            entityInceptionDt,
          };
        }

        // fallback for 'tabular'
        return {
          ...state,
          historicalPerformanceData: {
            ...state.historicalPerformanceData,
            [mode]: action.payload,
          },
          loading: false,
        };
      })
      .addCase(getPerformanceData.rejected, (state, action) => {
        state.historicalPerformanceError = action.payload;
        state.loading = false;
      });
  },
});

export const { setLoader, setCalendarPerformance, setSelectedPeriod, setSelectedCustomPeriod } =
  appSlice.actions;
export default appSlice.reducer;
